import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Document = styled.img`
    display: none;
    height: 70px;
    width: fit-content;
    background-color: #000;
    border-radius: 10px;
    &:hover{
        cursor: pointer;
        opacity: 0.8;
    }
`

const Card = styled.article`
    width: 650px;
    border-radius: 12px;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    transition: all 0.3s ease-in-out;
    animation: ${fadeIn} 0.5s ease-out;
    border: 1px solid ${({ theme }) => theme.primary + '20'};
    background: ${({ theme }) => theme.card};
    box-shadow: 0 4px 24px rgba(23, 92, 230, 0.15);

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 6px 30px rgba(23, 92, 230, 0.2);
    }

    @media (max-width: 1024px) { width: 550px; }
    @media (max-width: 768px) { width: 500px; }
    @media (max-width: 576px) { width: 320px; }
    @media (max-width: 375px) { width: 280px; }
`;

const Top = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
`;

const Image = styled.img`
    height: 56px;
    width: 56px;
    border-radius: 8px;
    object-fit: cover;
    background: ${({ theme }) => theme.primary + '10'};

    @media (max-width: 768px) {
        height: 45px;
        width: 45px;
    }
`;

const Info = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const Role = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_primary};

    @media (max-width: 768px) { font-size: 16px; }
`;

const Company = styled.h4`
    font-size: 15px;
    font-weight: 500;
    color: ${({ theme }) => theme.text_secondary};

    @media (max-width: 768px) { font-size: 14px; }
`;

const Details = styled.div`
    display: flex;
    gap: 12px;
    font-size: 14px;
    color: ${({ theme }) => theme.text_secondary + '99'};

    @media (max-width: 768px) { font-size: 12px; }
`;

const Description = styled.div`
    font-size: 15px;
    font-weight: 400;
    color: ${({ theme }) => theme.text_primary + '99'};
    line-height: 1.5;

    @media (max-width: 768px) { font-size: 14px; }
`;

const Skills = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

const Skill = styled.span`
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.text_primary};
    background: ${({ theme }) => theme.primary + '10'};
    padding: 4px 8px;
    border-radius: 4px;

    @media (max-width: 768px) { font-size: 12px; }
`;

const ExperienceCard = ({ experience }) => {
    const {
        img,
        role,
        company,
        date,
        location,
        desc,
        skills,
        doc
    } = experience;

    return (
        <Card>
            <Top>
                <Image
                    src={img}
                    alt={`${company} logo`}
                    loading="lazy"
                />
                <Info>
                    <Role>{role}</Role>
                    <Company>{company}</Company>
                    <Details>
                        <span>{date}</span>
                        <span>•</span>
                        <span>{location}</span>
                    </Details>
                </Info>
            </Top>

            {desc && (
                <Description>
                    {desc}
                </Description>
            )}

            {skills && (
                <Skills>
                    {skills.map((skill, index) => (
                        <Skill key={index}>
                            {skill}
                        </Skill>
                    ))}
                </Skills>
            )}

            {doc && (
                <a
                    href={doc}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`View certificate for ${role} at ${company}`}
                >
                    <img src={doc} alt="Certificate" style={{ display: 'none' }} />
                </a>
            )}
        </Card>
    );
};



export default React.memo(ExperienceCard);
